// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import {
    updateWishListLines as updateWishListLinesApi,
    UpdateWishListLinesApiParameter,
} from "@insite/client-framework/Services/WishListService";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import { WishListLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";

interface Props {
    apiParameter: UpdateWishListLinesApiParameter;
    apiResult: WishListLineCollectionModel;
}
type Parameter = {
    grouping: any[];
    ids: string[];
    currentGroup: string;
    shouldReloadLines?: boolean;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginLoadWishListLinesIfNeeded: HandlerType = props => {
    const dataViewParameter = props.getState().pages.myListDetails.loadWishListLinesParameter;
    props.dispatch({
        type: "Data/WishListLines/BeginLoadWishListLines",
        parameter: dataViewParameter,
    });
};

// clear selected line ids from state
export const RemoveSelectedIdsFromRedux: HandlerType = async props => {
    if (!props.parameter.ids.length) {
        return;
    }

    props.dispatch(setAllWishListLinesIsSelected({ isSelected: false, wishListLineIds: props.parameter.ids }));
    let waiting = false;
    props.dispatch(
        setGroupWishListLineIsSelected({
            wishListLineIds: props.parameter.ids,
            groupName: props.parameter.currentGroup,
            onComplete: () => {
                waiting = true;
            },
        }),
    );
    await waitFor(() => waiting);
};

export const PopulateApiParameter: HandlerType = props => {
    const state = props.getState();
    const { wishListId, productInfosByWishListLineId } = state.pages.staticList.wishListId
        ? state.pages.staticList
        : state.pages.myListDetails;
    if (!wishListId) {
        throw new Error("There was no wishListId defined on the pages.myListDetails state");
    }

    const changedSharedListLinesQuantities: SafeDictionary<number> = {};
    for (const wishListLineId in productInfosByWishListLineId) {
        const wishListLine = state.data.wishListLines.byId[wishListLineId];
        const qtyOrdered = productInfosByWishListLineId[wishListLineId]!.qtyOrdered;
        if (!wishListLine || !qtyOrdered || wishListLine.qtyOrdered === qtyOrdered) {
            continue;
        }

        changedSharedListLinesQuantities[wishListLineId] = qtyOrdered;
    }
    const properties = {
        isUpdateGroup: true,
        grouping: JSON.stringify(props.parameter.grouping),
    };
    props.apiParameter = {
        wishListId,
        changedSharedListLinesQuantities,
        includeListLines: true,
        properties,
    } as any;
};

export const RequestUpdateWishListLines: HandlerType = async props => {
    if (!props.parameter.grouping.length) {
        return;
    }
    props.apiResult = await updateWishListLinesApi(props.apiParameter);
    waitFor(() => !!props.apiResult.wishListLines);
};

export const ResetWishListData: HandlerType = props => {
    if (props.parameter.shouldReloadLines) {
        props.dispatch({
            type: "Data/WishListLines/Reset",
        });
        props.dispatch({
            type: "Data/WishLists/Reset",
        });
    }
};

export const chain = [
    DispatchBeginLoadWishListLinesIfNeeded,
    RemoveSelectedIdsFromRedux,
    PopulateApiParameter,
    RequestUpdateWishListLines,
    ResetWishListData,
];

const updateWishListLines = createHandlerChainRunner(chain, "UpdateWishListLines");
export default updateWishListLines;
